// GymTracker GraphQL API service
// Stödjer kroppsmätningar (vikt + muskel/fett/vatten-%) sedan 2026-07-25.

const API_URL_KEY = 'gymtracker_api_url';
let apiInstance = null;

// Shared selection of fields for a full session (exercises + per-set rows).
const SESSION_FIELDS = `
  id name notes status date startTime endTime durationSecondsOverride estimatedCalories
  gymSessionExercises {
    id order notes calorieTimeSeconds
    exerciseType { id name metValue tracksWeight tracksReps tracksDistance tracksDuration isBodyweight }
    sets { id order reps weight distanceMeters durationSeconds rpe isWarmup isCompleted notes status statusNote }
  }
`;

export function getApiUrl() {
  // 1. Runtime config injected by Docker entrypoint (GYMTRACKER_API_URL i docker-compose)
  const runtimeUrl = window.__APP_CONFIG__?.gymTrackerApiUrl;
  if (runtimeUrl) return runtimeUrl;
  // 2. Användarens sparade URL (setup-UI → localStorage)
  try { return localStorage.getItem(API_URL_KEY) || null; } catch { return null; }
}

export function saveApiUrl(url) {
  let normalized = url.trim().replace(/\/$/, '');
  if (!normalized.endsWith('/graphql')) normalized += '/graphql';
  try { localStorage.setItem(API_URL_KEY, normalized); } catch (e) { /* localStorage ej tillgängligt */ }
  if (apiInstance) apiInstance.baseURL = normalized;
}

export async function testApiUrl(url) {
  const base = url.trim().replace(/\/graphql\/?$/, '');
  const healthUrl = `${base}/health`;

  // Försök 1: normal fetch (kräver CORS-headers för att lyckas)
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 5000);
    const res = await fetch(healthUrl, { method: 'GET', signal: ctrl.signal });
    clearTimeout(t);
    return { ok: res.ok, message: res.ok ? 'API nåbar!' : `HTTP ${res.status}` };
  } catch (e) {
    if (e.name === 'AbortError') return { ok: false, message: 'Timeout – inget svar efter 5s' };
  }

  // Försök 2: no-cors – webbläsaren gör anropet utan att kräva CORS-headers.
  // Om servern svarar får vi en opaque response (status 0). Om servern är nere kastas ett fel.
  try {
    const ctrl2 = new AbortController();
    const t2 = setTimeout(() => ctrl2.abort(), 5000);
    await fetch(healthUrl, { method: 'GET', mode: 'no-cors', signal: ctrl2.signal });
    clearTimeout(t2);
    // Kom hit = servern svarar men CORS-headers saknas för denna origin
    return {
      ok: true,
      corsWarning: true,
      message: 'API svarar men CORS blockerar från denna origin. ' +
               'Lägg till din URL i CORS_ALLOWED_ORIGINS på servern, eller bygg om API-imagen med den senaste koden.',
    };
  } catch (e2) {
    if (e2.name === 'AbortError') return { ok: false, message: 'Timeout – inget svar efter 5s' };
    return { ok: false, message: 'Kan inte nå servern. Kontrollera att adressen är rätt och att API:et körs.' };
  }
}

class GymTrackerApi {
  constructor() {
    const stored = getApiUrl();
    this.baseURL = stored || 'https://gymapi.brasse-pc.eu/graphql';
  }

  async request(query, variables = {}, token = null) {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(this.baseURL, {
      method: 'POST', headers, body: JSON.stringify({ query, variables })
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`HTTP ${res.status}: ${text}`);
    }
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors[0].message || 'GraphQL error');
    return json.data;
  }

  async login(username, password) {
    const q = `mutation($u:String!,$p:String!){login(username:$u,password:$p){success token refreshToken sessionId expiration userName}}`;
    return this.request(q, { u: username, p: password });
  }
  async refreshAccessToken(sessionId, refreshToken) {
    const q = `mutation($s:String!,$r:String!){refreshAccessToken(sessionId:$s,refreshToken:$r){success accessToken newRefreshToken accessTokenExpiration sessionExpiration message}}`;
    return this.request(q, { s: sessionId, r: refreshToken });
  }
  async logout(sessionId) {
    const q = `mutation($s:String!){logoutAsync(sessionId:$s)}`;
    return this.request(q, { s: sessionId });
  }
  async validateSession(sessionId) {
    const q = `query($s:String!){validateSession(sessionId:$s){isValid requiresLogin message sessionExpiration}}`;
    return this.request(q, { s: sessionId });
  }
  async myLifts(token) {
    const q = `query{myLifts{id weight reps sets date notes status statusNote gymSessionId gymSessionSetId exerciseType{id name}}}`;
    return this.request(q, {}, token);
  }
  async getMuscleGroups(token) {
    const q = `query{muscleGroups{id name}}`;
    return this.request(q, {}, token);
  }
  async getMuscles(token) {
    const q = `query{muscles{id name muscleGroupId}}`;
    return this.request(q, {}, token);
  }
  async getExerciseTypes(token) {
    const q = `query{exerciseTypes{id name description metValue tracksWeight tracksReps tracksDistance tracksDuration isBodyweight exerciseTypeMuscles{muscle{id name}}}}`;
    return this.request(q, {}, token);
  }
  async createMuscleGroup(name, token) {
    const q = `mutation($name: String!){createMuscleGroup(input:{name:$name}){id name}}`;
    return this.request(q, { name }, token);
  }
  async createMuscle(name, muscleGroupId, token) {
    const q = `mutation($name: String!, $mgid: Int){createMuscle(input:{name:$name, muscleGroupId:$mgid}){id name}}`;
    return this.request(q, { name, mgid: muscleGroupId }, token);
  }
  async createExerciseType(input, token) {
    const q = `mutation($input: CreateExerciseTypeInput!){createExerciseType(input:$input){id name}}`;
    return this.request(q, { input }, token);
  }
  async createLift(liftInput, token) {
    const q = `mutation($input: CreateLiftInput!){createLift(input:$input){id}}`;
    return this.request(q, { input: liftInput }, token);
  }
  async updateMuscleGroup(id, name, token) {
    const q = `mutation($id: Int!, $name: String!){updateMuscleGroup(input:{id:$id,name:$name}){id name}}`;
    return this.request(q, { id, name }, token);
  }
  async deleteMuscleGroup(id, token) {
    const q = `mutation($id: Int!){deleteMuscleGroup(id:$id)}`;
    return this.request(q, { id }, token);
  }
  async updateMuscle(id, name, muscleGroupId, token) {
    const q = `mutation($id: Int!, $name: String!, $mgid: Int){updateMuscle(input:{id:$id,name:$name,muscleGroupId:$mgid}){id name muscleGroupId}}`;
    return this.request(q, { id, name, mgid: muscleGroupId }, token);
  }
  async deleteMuscle(id, token) {
    const q = `mutation($id: Int!){deleteMuscle(id:$id)}`;
    return this.request(q, { id }, token);
  }
  async updateExerciseType(input, token) {
    const q = `mutation($input: UpdateExerciseTypeInput!){updateExerciseType(input:$input){id name}}`;
    return this.request(q, { input }, token);
  }
  async deleteExerciseType(id, token) {
    const q = `mutation($id: Int!){deleteExerciseType(id:$id)}`;
    return this.request(q, { id }, token);
  }
  async deleteLift(id, token) {
    const q = `mutation($id: Int!){deleteLift(id:$id)}`;
    return this.request(q, { id }, token);
  }
  async getPersonalBests(token, mode = 'all') {
    const q = `query($m:String){personalBests(mode:$m){exerciseTypeId exerciseTypeName isFavorite repRecords{reps weight date liftId notes status statusNote gymSessionId sessionName estimated1Rm}}}`;
    return this.request(q, { m: mode }, token);
  }

  // ── PB-sökning: reps vid vikt / vikt vid reps, exakt eller närmast under/över ──
  async getPbLookup({ exerciseTypeId, weight = null, reps = null, mode = 'all' }, token) {
    const hit = 'weight reps date liftId status statusNote sessionName estimated1Rm count';
    const q = `query($e:Int!,$w:Float,$r:Int,$m:String){pbLookup(exerciseTypeId:$e,weight:$w,reps:$r,mode:$m){exerciseTypeId exerciseTypeName mode queryWeight queryReps estimatedReps estimatedWeight bestEstimated1Rm exact{${hit}} below{${hit}} above{${hit}} byWeight{${hit}} byReps{${hit}}}}`;
    return this.request(q, { e: exerciseTypeId, w: weight, r: reps, m: mode }, token);
  }

  // ── Favoritövningar (samma som övningsväljaren i appen) ──
  async addFavoriteExercise(exerciseTypeId, token) {
    return this.request(`mutation($id:Int!){addFavoriteExercise(exerciseTypeId:$id){id}}`, { id: exerciseTypeId }, token);
  }
  async removeFavoriteExercise(exerciseTypeId, token) {
    return this.request(`mutation($id:Int!){removeFavoriteExercise(exerciseTypeId:$id)}`, { id: exerciseTypeId }, token);
  }

  // ── Rekordstatus (träning/tävling/räknas ej) ──
  async getPbCandidates({ exerciseTypeId, reps, limit = 8, mode = 'all' }, token) {
    const q = `query($e:Int!,$r:Int!,$l:Int,$m:String){pbCandidates(exerciseTypeId:$e,reps:$r,limit:$l,mode:$m){id exerciseTypeId exerciseTypeName weight reps date notes status statusNote gymSessionId sessionName gymSessionSetId estimated1Rm isCurrentRecord}}`;
    return this.request(q, { e: exerciseTypeId, r: reps, l: limit, m: mode }, token);
  }
  async getLiftDetail(id, token) {
    const q = `query($id:Int!){liftDetail(id:$id){id exerciseTypeId exerciseTypeName weight reps date notes status statusNote gymSessionId sessionName gymSessionSetId estimated1Rm isCurrentRecord}}`;
    return this.request(q, { id }, token);
  }
  async setLiftStatus({ id, status, note = null }, token) {
    const q = `mutation($input:SetLiftStatusInput!){setLiftStatus(input:$input){id status statusNote}}`;
    return this.request(q, { input: { id, status, note } }, token);
  }
  async setSessionSetStatus({ setId, status, note = null }, token) {
    const q = `mutation($input:SetSessionSetStatusInput!){setSessionSetStatus(input:$input){id status statusNote}}`;
    return this.request(q, { input: { setId, status, note } }, token);
  }
  async getWorkoutStats(period, referenceDate, token) {
    const q = `query($p:String!,$d:String){workoutStats(period:$p,referenceDate:$d){totalLifts totalVolume muscleGroupStats{muscleGroupId muscleGroupName liftCount totalVolume} muscleStats{muscleId muscleName muscleGroupName liftCount totalVolume} exerciseStats{exerciseTypeId exerciseTypeName liftCount totalVolume}}}`;
    return this.request(q, { p: period, d: referenceDate }, token);
  }

  async getExerciseProgress(exerciseTypeId, period, referenceDate, token) {
    const q = `query($id:Int!,$p:String!,$d:String){exerciseProgress(exerciseTypeId:$id,period:$p,referenceDate:$d){exerciseTypeId exerciseTypeName points{date topWeight bestE1rm totalVolume totalReps bestReps}}}`;
    return this.request(q, { id: exerciseTypeId, p: period, d: referenceDate }, token);
  }

  // ── Profile ──
  async getProfile(token) {
    // Fallback till gamla fältuppsättningen om servern inte är uppdaterad än.
    try {
      const q = `query{myProfile{username displayName email bodyWeightKg heightCm birthYear sex}}`;
      return await this.request(q, {}, token);
    } catch (e) {
      const q = `query{myProfile{username displayName email bodyWeightKg}}`;
      return this.request(q, {}, token);
    }
  }
  async updateBodyWeight(bodyWeightKg, token) {
    const q = `mutation($w: Float){updateBodyWeight(bodyWeightKg:$w){username bodyWeightKg}}`;
    return this.request(q, { w: bodyWeightKg }, token);
  }
  // Kroppsdata för BMR/kroppssammansättning. null = rör ej, -1/"" = rensa.
  async updateProfile({ heightCm = null, birthYear = null, sex = null }, token) {
    const q = `mutation($h: Float,$y: Int,$s: String){updateProfile(heightCm:$h,birthYear:$y,sex:$s){username bodyWeightKg heightCm birthYear sex}}`;
    return this.request(q, { h: heightCm, y: birthYear, s: sex }, token);
  }

  // ── Aktiviteter (cardio/fristående pass) ──
  async getActivityTypes(token) {
    const q = `query{activityTypes{id key nameSv nameEn met category isDistanceBased isCardio iconKey}}`;
    return this.request(q, {}, token);
  }
  async getActivities({ from = null, to = null, limit = null } = {}, token) {
    const q = `query($f:DateTime,$t:DateTime,$l:Int){activities(from:$f,to:$t,limit:$l){id startedAt durationSeconds distanceMeters elevationGainMeters estimatedKcal rpe source externalId notes routePolyline activityType{id key nameSv category isDistanceBased isCardio iconKey}}}`;
    return this.request(q, { f: from, t: to, l: limit }, token);
  }
  async addActivity(input, token) {
    const q = `mutation($input: AddActivityInput!){addActivity(input:$input){id startedAt durationSeconds distanceMeters elevationGainMeters estimatedKcal rpe source notes activityType{id key nameSv category isDistanceBased isCardio iconKey}}}`;
    return this.request(q, { input }, token);
  }
  async updateActivity(input, token) {
    const q = `mutation($input: UpdateActivityInput!){updateActivity(input:$input){id startedAt durationSeconds distanceMeters elevationGainMeters estimatedKcal rpe source notes activityType{id key nameSv category isDistanceBased isCardio iconKey}}}`;
    return this.request(q, { input }, token);
  }
  async deleteActivity(id, token) {
    const q = `mutation($id: UUID!){deleteActivity(id:$id)}`;
    return this.request(q, { id }, token);
  }
  async getDailySummary(date, token) {
    const q = `query($d:DateTime){dailySummary(date:$d){date gymKcal activityKcal activeKcal gymSessionCount activityCount distanceMeters bmrKcalPerDay phoneKcal steps}}`;
    return this.request(q, { d: date }, token);
  }

  // ── Mål ──
  async getGoalsWithProgress({ dayStart, weekStart, monthStart }, token) {
    const q = `query($d:DateTime!,$w:DateTime!,$m:DateTime!){goalsWithProgress(dayStart:$d,weekStart:$w,monthStart:$m){id metric period targetValue currentValue}}`;
    return this.request(q, { d: dayStart, w: weekStart, m: monthStart }, token);
  }
  // targetValue <= 0 tar bort målet
  async setGoal(metric, period, targetValue, token) {
    const q = `mutation($m:String!,$p:String!,$t:Float!){setGoal(metric:$m,period:$p,targetValue:$t)}`;
    return this.request(q, { m: metric, p: period, t: targetValue }, token);
  }

  // ── Import (Daily Strength / Google Takeout) — batchmutationer, se docs/gym-import-plan.md ──
  async importExistingKeys({ from = null, to = null } = {}, token) {
    const q = `query($f:DateTime,$t:DateTime){importExistingKeys(from:$f,to:$t){sessions{externalId name startTime endTime date} activities{externalId startedAt durationSeconds} measurementDates steps{date steps}}}`;
    return this.request(q, { f: from, t: to }, token);
  }
  async getExerciseAliases(token) {
    return this.request(`query{exerciseAliases{alias exerciseTypeId}}`, {}, token);
  }
  async setExerciseAliases(input, token) {
    const q = `mutation($input:[ExerciseAliasInput!]!){setExerciseAliases(input:$input){alias exerciseTypeId}}`;
    return this.request(q, { input }, token);
  }
  async importGymSessions(input, token) {
    const q = `mutation($input:[ImportGymSessionInput!]!){importGymSessions(input:$input){created updated skipped failed{externalId label message}}}`;
    return this.request(q, { input }, token);
  }
  async importBodyMeasurements(input, token) {
    const q = `mutation($input:[ImportBodyMeasurementInput!]!){importBodyMeasurements(input:$input){created updated skipped failed{externalId label message}}}`;
    return this.request(q, { input }, token);
  }
  async importActivities(input, token) {
    const q = `mutation($input:[ImportActivityInput!]!){importActivities(input:$input){created updated skipped failed{externalId label message}}}`;
    return this.request(q, { input }, token);
  }
  async importDailySteps(input, token) {
    const q = `mutation($input:[ImportDailyStepsInput!]!){importDailySteps(input:$input){created updated skipped failed{externalId label message}}}`;
    return this.request(q, { input }, token);
  }

  // ── Body measurements (vikt + kroppssammansättning över tid) ──
  async getBodyMeasurements(token) {
    const q = `query{bodyMeasurements{id date weightKg musclePercent fatPercent waterPercent}}`;
    return this.request(q, {}, token);
  }
  async addBodyMeasurement(input, token) {
    // input: { weightKg, date?, musclePercent?, fatPercent?, waterPercent? }
    const q = `mutation($input: AddBodyMeasurementInput!){addBodyMeasurement(input:$input){id date weightKg musclePercent fatPercent waterPercent}}`;
    return this.request(q, { input }, token);
  }
  async updateBodyMeasurement(input, token) {
    // input: { id, weightKg?, date?, musclePercent?, fatPercent?, waterPercent? }  (-1 rensar procentfält)
    const q = `mutation($input: UpdateBodyMeasurementInput!){updateBodyMeasurement(input:$input){id date weightKg musclePercent fatPercent waterPercent}}`;
    return this.request(q, { input }, token);
  }
  async deleteBodyMeasurement(id, token) {
    const q = `mutation($id: UUID!){deleteBodyMeasurement(id:$id)}`;
    return this.request(q, { id }, token);
  }

  // ── Sessions ──
  async getActiveSession(token) {
    const q = `query{activeGymSession{${SESSION_FIELDS}}}`;
    return this.request(q, {}, token);
  }
  async getSessions(status, limit, offset, token) {
    const q = `query($s:String,$l:Int,$o:Int){gymSessions(status:$s,limit:$l,offset:$o){${SESSION_FIELDS}}}`;
    return this.request(q, { s: status, l: limit, o: offset }, token);
  }
  async getSession(id, token) {
    const q = `query($id:Int!){gymSession(id:$id){${SESSION_FIELDS}}}`;
    return this.request(q, { id }, token);
  }
  async getSessionStats(id, token) {
    const q = `query($id:Int!){gymSessionStats(id:$id){sessionId sessionName status date durationMinutes minDurationMinutes estimatedCalories totalSets totalReps totalVolumeKg totalDistanceMeters exerciseCount exerciseBreakdown{sessionExerciseId exerciseTypeId exerciseName totalSets totalReps maxWeight volumeKg distanceMeters timedSeconds allocatedSeconds estimatedCalories}}}`;
    return this.request(q, { id }, token);
  }
  async startSession(input, token) {
    const q = `mutation($input: StartGymSessionInput!){startGymSession(input:$input){${SESSION_FIELDS}}}`;
    return this.request(q, { input }, token);
  }
  async updateSession(input, token) {
    const q = `mutation($input: UpdateGymSessionInput!){updateGymSession(input:$input){id name notes date}}`;
    return this.request(q, { input }, token);
  }
  async completeSession(input, token) {
    const q = `mutation($input: CompleteGymSessionInput!){completeGymSession(input:$input){id status estimatedCalories durationSecondsOverride}}`;
    return this.request(q, { input }, token);
  }
  async deleteSession(id, token) {
    const q = `mutation($id: Int!){deleteGymSession(id:$id)}`;
    return this.request(q, { id }, token);
  }

  // ── Session exercises ──
  async addExercise(input, token) {
    const q = `mutation($input: AddSessionExerciseInput!){addExerciseToSession(input:$input){id order notes calorieTimeSeconds exerciseType{id name metValue tracksWeight tracksReps tracksDistance tracksDuration isBodyweight} sets{id order reps weight distanceMeters durationSeconds rpe isWarmup isCompleted notes}}}`;
    return this.request(q, { input }, token);
  }
  async updateSessionExercise(input, token) {
    const q = `mutation($input: UpdateSessionExerciseInput!){updateSessionExercise(input:$input){id order notes}}`;
    return this.request(q, { input }, token);
  }
  async removeExercise(id, token) {
    const q = `mutation($id: Int!){removeExerciseFromSession(id:$id)}`;
    return this.request(q, { id }, token);
  }

  // ── Sets ──
  async addSet(input, token) {
    const q = `mutation($input: AddSessionSetInput!){addSessionSet(input:$input){id order reps weight distanceMeters durationSeconds rpe isWarmup isCompleted notes}}`;
    return this.request(q, { input }, token);
  }
  async updateSet(input, token) {
    const q = `mutation($input: UpdateSessionSetInput!){updateSessionSet(input:$input){id order reps weight distanceMeters durationSeconds rpe isWarmup isCompleted notes}}`;
    return this.request(q, { input }, token);
  }
  async removeSet(id, token) {
    const q = `mutation($id: Int!){removeSessionSet(id:$id)}`;
    return this.request(q, { id }, token);
  }
  async getLastExercisePerformance(exerciseTypeId, excludeSessionId, token) {
    const q = `query($id:Int!,$ex:Int){lastExercisePerformance(exerciseTypeId:$id,excludeSessionId:$ex){sessionName date sets{reps weight distanceMeters durationSeconds rpe isWarmup}}}`;
    return this.request(q, { id: exerciseTypeId, ex: excludeSessionId }, token);
  }

  // ── Templates ──
  async getTemplates(token) {
    const q = `query{workoutTemplates{id name notes exercises{id order targetSets targetReps targetWeight targetDistanceMeters targetDurationSeconds progressionMetric progressionType progressionAmount notes exerciseType{id name tracksWeight tracksReps tracksDistance tracksDuration isBodyweight}}}}`;
    return this.request(q, {}, token);
  }
  async createTemplate(input, token) {
    const q = `mutation($input: CreateWorkoutTemplateInput!){createWorkoutTemplate(input:$input){id name}}`;
    return this.request(q, { input }, token);
  }
  async updateTemplate(input, token) {
    const q = `mutation($input: UpdateWorkoutTemplateInput!){updateWorkoutTemplate(input:$input){id name}}`;
    return this.request(q, { input }, token);
  }
  async saveSessionAsTemplate(sessionId, name, token) {
    const q = `mutation($id: Int!, $name: String){saveSessionAsTemplate(sessionId:$id, name:$name){id name}}`;
    return this.request(q, { id: sessionId, name }, token);
  }
  async deleteTemplate(id, token) {
    const q = `mutation($id: Int!){deleteWorkoutTemplate(id:$id)}`;
    return this.request(q, { id }, token);
  }

  // ── Session summary (statistics) ──
  async getSessionSummary(period, referenceDate, token) {
    const q = `query($p:String!,$d:String){gymSessionSummary(period:$p,referenceDate:$d){
      sessionCount totalDurationMinutes avgDurationMinutes totalCalories totalVolumeKg
      totalDistanceMeters totalDurationSecondsMeasured totalSets totalReps
      sessionsPerWeek currentStreakWeeks
      previous { sessionCount totalDurationMinutes avgDurationMinutes totalCalories totalVolumeKg totalSets }
      trend { start label bucket sessionCount totalVolumeKg totalCalories totalSets totalReps totalDistanceMeters totalDurationMinutes }
      weekdayCounts
      activeDays { date count }
      highlights {
        heaviestLift { exerciseName weight reps date }
        biggestSession { id name date value }
        longestSession { id name date value }
        newPrCount
      }
      sessions { id name date durationMinutes estimatedCalories exerciseCount totalSets totalReps totalVolumeKg totalDistanceMeters }
    }}`;
    return this.request(q, { p: period, d: referenceDate }, token);
  }
}

apiInstance = new GymTrackerApi();
export default apiInstance;
